import Route from '@ember/routing/route';
import { hash } from 'rsvp';
import { inject as service } from '@ember/service';
import { EMBER_ROUTE_NAMES } from 'sh-www/constants/routes';
import BuyerOnlyRouteMixin from 'sh-www/mixins/buyer-only-route-mixin';
import { trackPageViewFromTransition } from 'sh-www/utils/datalayer';
import {
  POSTING__ASSIGNMENT_SELLER_INCLUDES,
  POSTING_APPLICATIONS_SORT_API_ENUMS,
  POSTING_APPLICATIONS_FEATURED_COUNT,
} from 'sh-www/constants/routes/posting';
import { APPLICATION } from 'sh-www/models/application';

const postingBuyer = 'posting/buyer';

/**
 * @param {string} status
 * @param {string} path
 * @returns {*}
 */
export const routeFactory = (status, path) => {
  return class PostingIndexFactoryRoute extends Route.extend(BuyerOnlyRouteMixin) {
    /**
     * State manager for this route
     * @type {Service}
     */
    @service('commands/application-commands') applicationCommandService;
    @service('commands/company-commands') companyCommandService;
    @service router;
    @service session;
    @service('states/routes/posting') state;
    @service('state-managers/routes/posting') stateManager;

    /** @inheritDoc */
    get controllerName() {
      return postingBuyer;
    }

    /** @inheritDoc */
    get templateName() {
      return postingBuyer;
    }

    /**
     * @inheritDoc
     */
    model() {
      const { assignId } = this.paramsFor('posting');
      const {
        state,
        stateManager,
      } = this;
      const currentAssignment = state.assignment;

      this.controllerFor(postingBuyer).resetController(!!currentAssignment);

      const defaultSort = state.currentApplicationFilterStatus === APPLICATION.SIMPLE_STATUS.PENDING
        ? POSTING_APPLICATIONS_SORT_API_ENUMS.USER_SEARCH_RANK
        : POSTING_APPLICATIONS_SORT_API_ENUMS.LAST_ACTIVITY;

      const top5PendingApplications = this.applicationCommandService.loadApplications(
        assignId,
        APPLICATION.SIMPLE_STATUS.PENDING,
        POSTING_APPLICATIONS_SORT_API_ENUMS.USER_SEARCH_RANK,
        false,
        undefined,
        1,
        POSTING_APPLICATIONS_FEATURED_COUNT,
      );

      return hash({
        assignment: currentAssignment || stateManager.loadAssignmentForId(assignId),
        applicationList: stateManager.loadApplications(
          assignId,
          status,
          1,
          stateManager.POSTING_APPLICATIONS_PER_PAGE,
          defaultSort,
        ),
        blackListedUsers: this.companyCommandService.loadBlockedUsersForCompany(this.session.currentUser.company.id),
        featuredApplications: top5PendingApplications,
      });
    }

    /**
     * @inheritDoc
     */
    afterModel() {
      if (!this.state.assignment) {
        // we weren't able to load an assignment, send to 404 page
        this.router.transitionTo(EMBER_ROUTE_NAMES.PAGE_NOT_FOUND);
      }
    }

    /** @inheritDoc */
    activate(transition) {
      this._super(...arguments);
      trackPageViewFromTransition(this, transition, path);
    }

    /**
     * @inheritDoc
     */
    _redirectUnauthorizedUser() {
      const { assignId } = this.paramsFor('posting');
      this.router.transitionTo('posting', assignId);
    }
  }
}

/** @module */
class SellerPostingRoute extends Route {
  queryParams = {
    postingAdminSubSectionView: {
      replace: true,
    }
  };

  /** @type {Ember.Service} */
  @service('commands/assignment-commands') assignmentCommandService;

  /** @type {Ember.Service} */
  @service router;

  /** @type {Ember.Service} */
  @service session;

  /** @inheritDoc */
  beforeModel() {
    if (this.session.currentUser.isBuyer) {
      this.router.replaceWith(EMBER_ROUTE_NAMES.POSTING__PENDING, this.paramsFor('posting').assignId);
    }
  }

  /** @inheritDoc */
  model() {
    const { assignId } = this.paramsFor('posting');

    const assignmentIncludes = [...POSTING__ASSIGNMENT_SELLER_INCLUDES];

    if (this.session.currentUser.isAdmin) {
      assignmentIncludes.push('applications.seller');
      assignmentIncludes.push('applications.pitch');
      assignmentIncludes.push('assignment-statistics');
      assignmentIncludes.push('company-team');
    } else if (this.session.currentUser.isSeller) {
      assignmentIncludes.push('applications');
      assignmentIncludes.push('assignment-user-meta');
    } else {
      assignmentIncludes.push('assignment-user-meta');
    }

    return hash({
      assignment: this.assignmentCommandService.loadAssignment(assignId, assignmentIncludes),
    });
  }

  /** @inheritDoc */
  setupController(controller, model) {
    super.setupController(...arguments);

    if (this.session.currentUser.isAdmin) {
      controller.loadAssignmentInvitesTask.perform(model.assignment);
    }
  }

  /** @inheritDoc */
  resetController(controller, isExiting) {
    super.resetController(...arguments);

    if (isExiting) {
      controller.loadAssignmentInvitesTask.cancelAll();
    }
  }
}

export default SellerPostingRoute;
